import { useStore } from "@/state/store";
import { Pill } from "@/components/atoms/Pill";
import { ProbabilityRing } from "@/components/atoms/ProbabilityRing";
import { Sparkline } from "@/components/atoms/Sparkline";
import { Icon } from "@/components/atoms/Icons";
import {
  fmtMarketCount,
  fmtMarketMetric,
  fmtShortDate,
} from "@/components/atoms/format";
import { marketMetrics, type MarketWithMetrics } from "@/api/marketMetrics";
import { useMid } from "./useMid";
import { useOhlc, closes } from "./useOhlc";

type MarketView = MarketWithMetrics;

interface Props {
  market: MarketView;
  index?: number;
}

const CATEGORY_DOTS: Record<string, string> = {
  crypto: "#F7A23B",
  politics: "#5B7FFF",
  sports: "#2EE6A8",
  tech: "#8AA6FF",
  science: "#5B7FFF",
  culture: "#FF6B6B",
  macro: "#C5CEDB",
};

export function MarketCard({ market, index = 0 }: Props) {
  const openDetail = useStore((s) => s.openDetail);
  const openTrade = useStore((s) => s.openTrade);
  const isMulti = market.outcomes.length > 2;
  const firstOutcome = market.outcomes[0];
  const secondOutcome = market.outcomes[1];
  const mid = useMid(market.slug);

  // Cards multi → mostriamo lista compatta. Cards binary → ring + due bottoni.
  if (isMulti) {
    return (
      <MultiOutcomeCard market={market} index={index} />
    );
  }

  const yesPrice = mid;
  const noPrice = Math.max(0, Math.min(1, 1 - mid));
  // OHLC reale (bucket 5m, ultimo giorno). Se nessun trade è ancora arrivato,
  // points è vuoto e la sparkline non viene renderizzata.
  const { points } = useOhlc(market.slug, "5m", "1d", 20_000);
  const series = closes(points);

  return (
    <div
      className="card card-in flex flex-col gap-3 p-4"
      style={{ animationDelay: `${index * 30}ms` }}
    >
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full"
          style={{ background: CATEGORY_DOTS[market.category] ?? "#5B7FFF" }}
        />
        <span className="text-[11px] uppercase tracking-wider text-muted">
          {market.category}
        </span>
        <div className="flex-1" />
        {market.status === "open" && <Pill variant="accent">OPEN</Pill>}
        {market.status === "resolved" && <Pill variant="yes">RESOLVED</Pill>}
        {market.status === "closed" && <Pill variant="warn">CLOSED</Pill>}
      </div>

      <button
        type="button"
        onClick={() => openDetail(market.slug)}
        className="text-left transition-colors hover:text-accent-2"
      >
        <h3 className="line-clamp-2 text-[15.5px] font-semibold leading-[1.32]">
          {market.title}
        </h3>
      </button>

      <div className="flex items-center gap-4">
        <ProbabilityRing value={yesPrice * 100} size={72} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-muted flex items-center gap-1">
            <Icon.Clock size={11} />
            Resolves {fmtShortDate(market.resolve_after)}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span
              className="font-mono text-[22px] font-semibold"
              style={{ color: "var(--yes)" }}
            >
              {(yesPrice * 100).toFixed(1)}%
            </span>
            <span className="text-[11px] uppercase tracking-wider text-muted">YES</span>
          </div>
          {series.length >= 2 && (
            <div className="mt-1">
              <Sparkline values={series} width={160} height={32} />
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button
          className="btn-yn yes"
          disabled={!firstOutcome}
          onClick={() =>
            firstOutcome &&
            openTrade(market, firstOutcome.id, "buy")
          }
        >
          <span>YES</span>
          <span className="font-mono">{(yesPrice * 100).toFixed(1)}¢</span>
        </button>
        <button
          className="btn-yn no"
          disabled={!secondOutcome}
          onClick={() =>
            secondOutcome &&
            openTrade(market, secondOutcome.id, "buy")
          }
        >
          <span>NO</span>
          <span className="font-mono">{(noPrice * 100).toFixed(1)}¢</span>
        </button>
      </div>

      <MarketMetricsRow market={market} />
    </div>
  );
}

/** Riga metriche in fondo alla card: volume, liquidità e numero di trade.
 *  Le label restano mute, i valori in mono per allinearsi verticalmente
 *  fra card adiacenti nella griglia. */
function MarketMetricsRow({ market }: { market: MarketView }) {
  const { volume, liquidity, tradeCount } = marketMetrics(market);
  return (
    <div className="flex items-center justify-between gap-2 text-[11px] text-muted">
      <span className="flex items-center gap-1">
        <Icon.Volume size={11} />
        <span>Vol.</span>
        <span className="font-mono text-text-2">{fmtMarketMetric(volume)}</span>
      </span>
      <span className="flex items-center gap-1">
        <Icon.Droplet size={11} />
        <span>Liq.</span>
        <span className="font-mono text-text-2">
          {fmtMarketMetric(liquidity)}
        </span>
      </span>
      <span className="flex items-center gap-1">
        <Icon.Users size={11} />
        <span className="font-mono text-text-2">
          {fmtMarketCount(tradeCount)}
        </span>
      </span>
    </div>
  );
}

function MultiOutcomeCard({ market, index }: { market: MarketView; index: number }) {
  const openDetail = useStore((s) => s.openDetail);
  const openTrade = useStore((s) => s.openTrade);
  const top = market.outcomes.slice(0, 4);
  // Stima provvisoria: distribuzione uniforme (sostituita da prezzi reali quando wireremo
  // un endpoint summary per outcome).
  const fair = top.length > 0 ? 1 / top.length : 0;

  return (
    <div
      className="card card-in flex flex-col gap-3 p-4"
      style={{ animationDelay: `${index * 30}ms` }}
    >
      <div className="flex items-center gap-2">
        <span
          className="h-2 w-2 rounded-full"
          style={{ background: CATEGORY_DOTS[market.category] ?? "#5B7FFF" }}
        />
        <span className="text-[11px] uppercase tracking-wider text-muted">
          {market.category}
        </span>
        <Pill variant="accent">MULTI</Pill>
      </div>

      <button
        type="button"
        onClick={() => openDetail(market.slug)}
        className="text-left transition-colors hover:text-accent-2"
      >
        <h3 className="line-clamp-2 text-[15.5px] font-semibold leading-[1.32]">
          {market.title}
        </h3>
      </button>

      <div className="flex flex-col gap-2">
        {top.map((o) => (
          <div
            key={o.id}
            className="flex items-center gap-2 rounded-[10px] border border-line bg-bg-2 px-2 py-[6px]"
          >
            <span className="flex-1 truncate text-[12px] text-text-2">
              {o.label}
            </span>
            <span className="font-mono text-[12px] text-muted">
              {(fair * 100).toFixed(0)}%
            </span>
            <button
              className="btn-yn yes !py-1 !px-2 !text-[11px]"
              onClick={() => openTrade(market, o.id, "buy")}
            >
              YES
            </button>
          </div>
        ))}
      </div>

      {market.outcomes.length > 4 && (
        <div className="text-[11px] text-muted">
          + {market.outcomes.length - 4} more outcomes
        </div>
      )}

      <MarketMetricsRow market={market} />
    </div>
  );
}
